import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { MenuItem, CartItem } from '@/types/menu';

interface PendingOrderData {
  cartItems: CartItem[];
  specialInstructions?: string;
  tipPercentage?: number;
  storeId?: string;
  tableId?: string;
  totalAmount?: number;
  timestamp?: string;
}

export function useCart(tableId?: string) {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [selectedTip, setSelectedTip] = useState(0);

  // Load cart data from localStorage on mount
  useEffect(() => {
    if (tableId) {
      try {
        const savedCart = localStorage.getItem(`cart_${tableId}`);
        if (savedCart) {
          const cartData = JSON.parse(savedCart);
          setCartItems(cartData.cartItems || []);
          setSpecialInstructions(cartData.specialInstructions || '');
          setSelectedTip(cartData.selectedTip || 0);
        }
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
      }
    }
  }, [tableId]);

  // Save cart data to localStorage whenever it changes
  useEffect(() => {
    if (tableId) {
      try {
        const cartData = {
          cartItems,
          specialInstructions,
          selectedTip,
          timestamp: new Date().toISOString()
        };
        localStorage.setItem(`cart_${tableId}`, JSON.stringify(cartData));
      } catch (error) {
        console.error('Error saving cart to localStorage:', error);
      }
    }
  }, [cartItems, specialInstructions, selectedTip, tableId]);

  // Calculate totals when cart items change
  useEffect(() => {
    const newTotalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    const newTotalAmount = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    setTotalItems(newTotalItems);
    setTotalAmount(newTotalAmount);
  }, [cartItems]);

  // Calculate total amount
  const calculateTotal = () => {
    const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const tipAmount = (subtotal * selectedTip) / 100;
    return subtotal + tipAmount;
  };

  // Calculate subtotal (before tip)
  const calculateSubtotal = () => {
    return cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  // Calculate tip amount
  const calculateTipAmount = () => {
    const subtotal = calculateSubtotal();
    return (subtotal * selectedTip) / 100;
  };

  // Add an item to the cart
  const addItemToCart = (item: {
    id: string;
    name: string;
    price: number;
    quantity: number;
    imageUrl?: string;
    notes?: string;
  }) => {
    // First, ensure the price is a number
    const price = typeof item.price === 'string' ? parseFloat(item.price) : item.price;
    
    setCartItems(prevItems => {
      // Check if item already exists in cart - use item.id as menuItemId
      const existingItemIndex = prevItems.findIndex(
        cartItem => cartItem.menuItemId === item.id
      );

      if (existingItemIndex >= 0) {
        // Item exists, update quantity
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex].quantity += item.quantity;
        return updatedItems;
      } else {
        // Item doesn't exist, add new item
        return [
          ...prevItems,
          {
            id: uuidv4(),
            menuItemId: item.id, // Use the passed id as menuItemId
            menuItemName: item.name,
            quantity: item.quantity,
            price: price,
            imageUrl: item.imageUrl || '',
            notes: item.notes
          }
        ];
      }
    });
  };

  // Update item quantity in cart
  const updateItemQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      removeItemFromCart(id);
      return;
    }

    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === id ? { ...item, quantity } : item
      )
    );
  };

  // Update quantity by delta
  const updateQuantity = (id: string, delta: number) => {
    setCartItems(prevItems => {
      const item = prevItems.find(item => item.id === id);
      if (!item) return prevItems;
      
      const newQuantity = item.quantity + delta;
      
      if (newQuantity <= 0) {
        return prevItems.filter(item => item.id !== id);
      }
      
      return prevItems.map(item => 
        item.id === id ? { ...item, quantity: newQuantity } : item
      );
    });
  };

  // Remove item from cart
  const removeItemFromCart = (id: string) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== id));
  };

  // Remove specific item
  const removeItem = (id: string) => {
    removeItemFromCart(id);
  };

  // Clear the cart
  const clearCart = () => {
    setCartItems([]);
    setSpecialInstructions('');
    setSelectedTip(0);
    // Also clear from localStorage
    if (tableId) {
      try {
        localStorage.removeItem(`cart_${tableId}`);
      } catch (error) {
        console.error('Error clearing cart from localStorage:', error);
      }
    }
  };

  // Update item notes
  const updateItemNotes = (id: string, notes: string) => {
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === id ? { ...item, notes } : item
      )
    );
  };

  // Restore cart from pending order data (for payment failures)
  const restoreCartFromPendingOrder = (pendingOrderData: PendingOrderData) => {
    if (pendingOrderData.cartItems) {
      setCartItems(pendingOrderData.cartItems);
      setSpecialInstructions(pendingOrderData.specialInstructions || '');
      setSelectedTip(pendingOrderData.tipPercentage || 0);
    }
  };

  return {
    cartItems,
    totalAmount,
    totalItems,
    itemCount: totalItems,
    specialInstructions,
    setSpecialInstructions,
    selectedTip,
    setSelectedTip,
    addItemToCart,
    updateItemQuantity,
    updateQuantity,
    removeItemFromCart,
    removeItem,
    clearCart,
    updateItemNotes,
    calculateTotal,
    calculateSubtotal,
    calculateTipAmount,
    restoreCartFromPendingOrder
  };
}
